import { RouteErrorView } from 'components/App/components/ErrorFallback/RouteErrorView'
import ContentLayout from 'components/ContentLayout'
import { CuratedStatusesFolderView } from 'components/CuratedFolderView'
import StreamView from 'components/StreamView'
import { URL_SEGMENT_FEED, URL_SEGMENT_FILE_FOLDER, URL_SEGMENT_STATUS } from 'config'
import {
  ContentSource,
  Feed,
  InteractionEvent,
  RangeFilter,
  Stream
} from 'interfaces'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation, useMatch, useNavigate, useParams } from 'react-router'
import MyUploadsFolderView from 'routes/find/routes/my-uploads/routes/MyUploadsFolderView'
import STATUSES_CATEGORIES, { statusIdeaFeeds } from 'routes/find/routes/statuses/statuses.config'
import { forkJoin, from, Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CONTENT_TYPE_TO_URL_SEGMENT_MAP, URL_SEGMENT_TO_CONTENT_TYPE_MAP } from 'services/content/adapters/mappings'
import { addRecentSource, getRecentSources, trackStatusIdeasCategoryInteraction } from 'services/content/recommended/actions'
import { recentSourcesSelector } from 'services/content/selectors'
import { getStreams } from 'services/content/streams/actions'
import { getUploads } from 'services/uploads/actions'
import { StoreThunkDispatch } from 'store/state'
import * as FilterStore from 'utils/filter-store'
import RecentlyViewedRootView from './components/RecentlyViewedRootView'
import { ContentType, FilterType } from 'shared'

const BASE_URL = '/content/recently-viewed'


function RecentlyViewed() {
  const [loading, setLoading] = useState(true)
  const unmount$ = useRef<Subject<boolean> | null>(null)
  const subscription = useRef<Subscription | null>(null)

  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()

  useEffect(() => {
    unmount$.current = new Subject()

    const getRecentSources$ = from(dispatch(getRecentSources(1)).unwrap())
    const getUserStreams$ = from(dispatch(getStreams(false)).unwrap())
    const getFileFolders$ = from(dispatch(getUploads(false)).unwrap())

    subscription.current = forkJoin([getRecentSources$, getUserStreams$, getFileFolders$])
      .pipe(takeUntil(unmount$.current as Subject<boolean>))
      .subscribe(
        () => setLoading(false),
        (err) => {
          console.error('[RV fetch data] Error: ', err)
          setLoading(false)
        }
      )

    return () => {
      unmount$.current?.next(true)
      subscription.current?.unsubscribe()
    }
  }, [dispatch])

  return (
    <div data-test="recently-viewed">
      <Routes>
        <Route
          path={`${URL_SEGMENT_FILE_FOLDER}/:folderId`}
          element={
            <MyUploadsFolderView
              backUrl={(location.state as any)?.backUrl || BASE_URL}
            />
          }
          errorElement={<RouteErrorView />}
        />
        <Route
          path={`${URL_SEGMENT_STATUS}/:category`}
          element={<StatusIdeaRoute />}
          errorElement={<RouteErrorView />}
        />
        <Route
          path={`${URL_SEGMENT_FEED}/:id/:filter/:range`}
          element={<StreamFeedRoute />}
          errorElement={<RouteErrorView />}
        />
        <Route
          path=":type/:id/*"
          element={<StreamFeedRoute />}
          errorElement={<RouteErrorView />}
        />
        <Route
          path="/"
          element={<RecentlyViewedRootView />}
          errorElement={<RouteErrorView />}
        />
      </Routes>
    </div>
  )
}

interface RecentlyViewedStreamRouteProps {
  stream: Stream
  onAddRecentSource: (source: ContentSource) => void
  onNavigateBack: (url?: string) => void
}

function RecentlyViewedStreamRoute(props: RecentlyViewedStreamRouteProps) {
  const { stream, onAddRecentSource, onNavigateBack } = props
  // EXPL: keep latest onAddRecentSource in a ref — it's a non-memoized parent callback, so the
  // effect must stay keyed on `stream` only to add the source once per stream change, not per render.
  const onAddRecentSourceRef = useRef(onAddRecentSource)
  useEffect(() => {
    onAddRecentSourceRef.current = onAddRecentSource
  })

  useEffect(() => {
    onAddRecentSourceRef.current(stream)
  }, [stream])

  return (
    <StreamView
      stream={stream}
      showBackLink
      navigation={{ prefix: `${BASE_URL}/stream/${stream.id}`, id: stream.id }}
      onBackClick={onNavigateBack}
    />
  )
}

function StreamFeedRoute() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const matchParams = useParams() as { id: string, filter: string, type: string, range: string }
  const feedMatch = useMatch('/content/recently-viewed/:type/:id/:filter/:range')
  const location = useLocation()
  const recentSources = useSelector(recentSourcesSelector)
  const { id, filter, type } = matchParams
  const range = matchParams.range as RangeFilter
  const validRange = FilterStore.assureValidRange(range)
  let validFilter = FilterStore.assureValidType(URL_SEGMENT_TO_CONTENT_TYPE_MAP[filter || ''])
  const source = id && recentSources.length !== 0 ? recentSources.find(item => item.id === id) : undefined
  const sourceFilters = (source as any)?.sources || (source as any)?.filters
  // Do not check "all" and "stock-all" filters...
  if (sourceFilters && !validFilter.includes('all') && !sourceFilters.includes(validFilter as ContentType)) {
    validFilter = sourceFilters[0]
  }

  const navigateBack = React.useCallback(() => {
    navigate(location.state?.backUrl || BASE_URL)
  }, [navigate, location.state?.backUrl])

  const onFilterChanged = React.useCallback((filter: FilterType) => {
    const newPath = feedMatch?.pattern.path.replace(':id', matchParams.id)
      .replace(':type', URL_SEGMENT_FEED)
      .replace(':filter', CONTENT_TYPE_TO_URL_SEGMENT_MAP[filter])
      .replace(':range', matchParams.range)

    if (newPath) {
      navigate(newPath, { replace: true })
    }
  }, [navigate, matchParams.id, matchParams.range, feedMatch?.pattern.path])

  const onRangeChanged = React.useCallback((range: RangeFilter) => {
    const nextPath = feedMatch?.pattern.path.replace(':id', matchParams.id)
      .replace(':type', URL_SEGMENT_FEED)
      .replace(':filter', matchParams.filter)
      .replace(':range', range)

    if (nextPath) {
      navigate(nextPath, { replace: true })
    }
  }, [feedMatch?.pattern.path, matchParams.id, matchParams.filter, navigate])

  // EXPL: If requested source is not in user's most recent, redirect to root source url: /content/feeds(streams)/id
  if (id && !source) {
    const type = matchParams.type || feedMatch?.params.type
    const rootSourceUrl = `/content/${type}s/${id}`
    return <Navigate to={rootSourceUrl} />
  }

  const onAddRecent = () => {
    if (source) {
      dispatch(addRecentSource(source))
    }
  }

  const onNavigateBackFromStream = () => {
    if (location.state?.backUrl) {
      navigate(location.state?.backUrl)
    } else {
      navigateBack()
    }
  }

  if (source && type === 'stream') {
    return (
      <RecentlyViewedStreamRoute
        stream={source as Stream}
        onAddRecentSource={onAddRecent}
        onNavigateBack={onNavigateBackFromStream}
      />
    )
  }

  // EXPL: If range or filter are missing in url or invalid, get them from the store and reload
  if (id && source && type === 'feed' && (range !== validRange || filter !== CONTENT_TYPE_TO_URL_SEGMENT_MAP[validFilter])) {
    const souceFilters = (source as Feed).sources
    const storedFilters = FilterStore.getFeedFilters(source as Feed)
    const isUrlFilterValid = souceFilters.includes(URL_SEGMENT_TO_CONTENT_TYPE_MAP[filter] as ContentType)
    const validFilterForFeed = souceFilters.includes(storedFilters.type as ContentType)
      ? CONTENT_TYPE_TO_URL_SEGMENT_MAP[storedFilters.type]
      : CONTENT_TYPE_TO_URL_SEGMENT_MAP[souceFilters[0]]
    const sourceFilter = isUrlFilterValid ? filter : validFilterForFeed
    const sourceRange = range || storedFilters.range
    return <Navigate to={{ pathname: `${BASE_URL}/${type}/${id}/${sourceFilter}/${sourceRange}` }} state={location.state} />
  }

  if (!source) {
    return null
  }

  const contentTypeFilter = URL_SEGMENT_TO_CONTENT_TYPE_MAP[filter]

  return (
    <ContentLayout
      range={range}
      filter={contentTypeFilter}
      key={source.id}
      source={source as any}
      expanded
      onCollapsed={navigateBack}
      onFilterChanged={onFilterChanged}
      onRangeChanged={onRangeChanged}
    />
  )
}

function StatusIdeaRoute() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const matchParams = useParams<{ category: string }>()
  const location = useLocation()
  const category = STATUSES_CATEGORIES.find(cat => cat.slug === matchParams.category)
  const feed = statusIdeaFeeds.find(f => f.handle === matchParams.category)

  if (!category) {
    return <Navigate to="/content/statuses" />
  }

  const recordInteraction = (categoryIds: string[], event: InteractionEvent) => {
    if (feed) {
      dispatch(addRecentSource(feed))
    }

    return dispatch(trackStatusIdeasCategoryInteraction({ categoryIds, event })).unwrap()
  }

  const navigateBack = () => {
    navigate(location.state?.backUrl || BASE_URL)
  }

  return (
    <CuratedStatusesFolderView
      folder={category}
      navigation={{
        more: '',
        back: intl.formatMessage({ id: 'general.carousel.nav.back' }),
        prefix: `${BASE_URL}/${URL_SEGMENT_STATUS}`
      }}
      onBackClick={navigateBack}
      trackInteraction={recordInteraction}
    />
  )
}

export default RecentlyViewed
